<script setup>
/* eslint-disable no-unused-vars */
import { ref, reactive, onMounted, computed } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

const loading = ref(true);
const enviando = ref(false);
const solicitudes = ref([]);
const mostrarFormulario = ref(false);
const activoBuscado = ref(null);
const codigoBusqueda = ref('');

const form = reactive({
  activo: null,
  tipo_solicitud: 'MANTENIMIENTO',
  severidad: '2',
  descripcion_problema: '',
  requerimiento: '',
});

// Validación: Descripción y requerimiento mínimo 10 caracteres
const formularioValido = computed(() => {
  return form.activo &&
    form.descripcion_problema.trim().length >= 10 &&
    form.requerimiento.trim().length >= 5;
});

const enviarSolicitud = async () => {
  enviando.value = true;
  try {
    await api.post('solicitudes/', { ...form });
    Swal.fire('¡Enviado!', 'Tu ticket ha sido registrado. El equipo de TI ha sido notificado.', 'success');
    resetForm();
    mostrarFormulario.value = false;
    await obtenerMisSolicitudes();
  } catch (error) {
    Swal.fire('Error', 'No se pudo enviar. Revisa los campos.', 'error');
  } finally { enviando.value = false; }
};

const buscarActivo = async () => {
  try {
    const res = await api.get(`activos/?search=${codigoBusqueda.value}`);
    const results = res.data.results || res.data;
    if (results.length > 0) {
      activoBuscado.value = results[0];
      form.activo = results[0].id;
    } else {
      Swal.fire('No encontrado', 'Código de equipo inválido', 'warning');
    }
  } catch (e) { console.error(e); }
};

const obtenerMisSolicitudes = async () => {
  try {
    const res = await api.get('solicitudes/'); // Django filtra automáticamente por el usuario logueado
    solicitudes.value = res.data.results || res.data;
  } catch (e) { console.error(e); }
  finally { loading.value = false; }
};

const resetForm = () => {
  form.activo = null; form.descripcion_problema = ''; form.requerimiento = '';
  activoBuscado.value = null; codigoBusqueda.value = '';
};

onMounted(() => {
  obtenerMisSolicitudes();
});
</script>

<template>
  <div class="container-fluid py-4 app-transition">
    <div class="card shadow-lg border-0 border-radius-xl overflow-hidden card-custom mb-4">
      <div class="header-tamep px-4 py-3 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="logo-container-left me-3 p-1 rounded-lg shadow-sm"
            style="border-radius: 8px; background-color: #ffffff !important;">
            <img :src="logo" class="logo-img p-1" style="max-height: 40px;">
          </div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="text-white font-weight-bolder text-uppercase mb-0">Centro de Ayuda</h6>
            <small class="text-white opacity-8">Reporta fallas técnicas de tus equipos asignados</small>
          </div>
        </div>
        <button class="btn mb-0 shadow-sm font-weight-bolder"
          :class="mostrarFormulario ? 'btn-danger' : 'btn-warning text-dark'"
          @click="mostrarFormulario = !mostrarFormulario">
          <i class="fas" :class="mostrarFormulario ? 'fa-times' : 'fa-plus me-2'"></i>
          {{ mostrarFormulario ? 'Cancelar' : 'Nuevo Reporte' }}
        </button>
      </div>
    </div>

    <transition name="slide-fade">
      <div v-if="mostrarFormulario"
        class="card card-custom shadow-lg mb-4 animate__animated animate__fadeIn border-radius-xl overflow-hidden">
        <div class="card-body p-4 bg-section-custom">
          <div class="row">
            <div class="col-md-5 border-end">
              <h6 class="text-sm font-weight-bold">1. Localiza tu Equipo</h6>
              <div class="input-group mb-3">
                <input type="text" class="form-control" v-model="codigoBusqueda" placeholder="Código TAMEP"
                  @keyup.enter="buscarActivo">
                <button class="btn btn-dark mb-0" @click="buscarActivo"><i class="fas fa-search"></i></button>
              </div>
              <div v-if="activoBuscado" class="bg-gray-100 p-3 border-radius-lg">
                <p class="text-xs mb-0"><strong>Equipo:</strong> {{ activoBuscado.nombre_especifico }}</p>
                <p class="text-xs mb-0"><strong>Ubicación:</strong> {{ activoBuscado.ubicacion_nombre || 'Sede Central'
                  }}</p>
              </div>
            </div>
            <div class="col-md-7 ps-md-4">
              <h6 class="text-sm font-weight-bold">2. Cuéntanos qué sucede</h6>
              <textarea class="form-control mb-3" v-model="form.descripcion_problema" rows="2"
                placeholder="Descripción de la falla..."></textarea>
              <input type="text" class="form-control mb-3" v-model="form.requerimiento"
                placeholder="¿Qué necesitas que haga el técnico?">
              <button class="btn btn-primary w-100" @click="enviarSolicitud" :disabled="!formularioValido || enviando">
                Enviar Ticket a Soporte
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="card card-custom shadow-lg border-radius-xl overflow-hidden">
      <div class="card-header card-header-custom pb-0 p-4 border-bottom mb-2">
        <h6 class="text-title-custom text-uppercase font-weight-bolder mb-0">Historial de Mis Reportes</h6>
      </div>
      <div class="card-body bg-section-custom px-0 pt-0 pb-2">
        <div class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Ticket</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Equipo</th>
                <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sol in solicitudes" :key="sol.id">
                <td class="ps-4"><span class="text-xs font-weight-bold">#TKT-{{ String(sol.id).padStart(4, '0')
                    }}</span></td>
                <td>
                  <p class="text-xs mb-0">{{ sol.activo_codigo }}</p>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm"
                    :class="sol.estado_texto === 'Pendiente' ? 'bg-gradient-danger' : 'bg-gradient-success'">
                    {{ sol.estado_texto }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition {
  transition: all 0.3s ease;
}

.header-tamep {
  background: linear-gradient(135deg, #003366 0%, #001d3d 100%);
}

.card-custom {
  background-color: #ffffff;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.card-header-custom {
  background-color: transparent;
}

.bg-section-custom {
  background-color: #f8f9fa;
}

.text-title-custom {
  color: #344767;
}
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom,
.dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .bg-section-custom,
.dark-mode .bg-section-custom {
  background-color: #121212 !important;
}

.dark-version .bg-gray-100,
.dark-mode .bg-gray-100 {
  background-color: #2a2a2a !important;
  border: 1px solid #333 !important;
}

.dark-version .table,
.dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th,
.dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td,
.dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version .form-control,
.dark-mode .form-control {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-control:focus,
.dark-mode .form-control:focus {
  border-color: #4dabf7 !important;
}

.dark-version .text-title-custom,
.dark-mode .text-title-custom {
  color: #ffffff !important;
}

.dark-version h6,
.dark-mode h6 {
  color: #ffffff !important;
}

.dark-version p,
.dark-mode p {
  color: #e0e0e0 !important;
}

.dark-version strong,
.dark-mode strong {
  color: #4dabf7 !important;
}
</style>